import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CTAButton from "../components/common/CTAButton";
import { useAuth } from "../contexts/AuthContext";
import { fetchQuestions, submitCheckIn } from "../services/checkInApi";
import "./CheckInPage.css";

const CheckInPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasMountedRef = useRef(false);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const data = await fetchQuestions();
        setQuestions(data);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load check-in questions.");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentDomainIndex]);

  const groupedDomains = useMemo(() => {
    const domainMap = new Map();

    questions.forEach((q) => {
      const key = q.domain_code || "general";

      if (!domainMap.has(key)) {
        domainMap.set(key, {
          domain_code: key,
          domain_label: formatDomainLabel(key),
          questions: [],
        });
      }

      domainMap.get(key).questions.push(q);
    });

    return Array.from(domainMap.values());
  }, [questions]);

  const totalDomains = groupedDomains.length;
  const currentDomain = groupedDomains[currentDomainIndex];
  const isFirstStep = currentDomainIndex === 0;
  const isLastStep = currentDomainIndex === totalDomains - 1;
  const backTarget = user ? "/dashboard" : "/";
  const backLabel = user ? "Back to Dashboard" : "Back to Home";

  const answeredCount = Object.values(answers).filter(
    (value) => value !== undefined && value !== null && value !== "",
  ).length;

  const progressPercentage =
    totalDomains > 0 ? ((currentDomainIndex + 1) / totalDomains) * 100 : 0;

  const handleChange = (questionCode, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionCode]: value,
    }));
  };

  const handleNext = () => {
    setError("");
    setCurrentDomainIndex((prev) => Math.min(prev + 1, totalDomains - 1));
  };

  const handlePrevious = () => {
    setError("");
    setCurrentDomainIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSkipDomain = () => {
    setError("");
    setCurrentDomainIndex((prev) => Math.min(prev + 1, totalDomains - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const result = await submitCheckIn(answers);

      navigate("/check-in/result", {
        state: {
          assessment: result,
          saved: result.saved,
          answers,
        },
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to submit check-in.");
    } finally {
      setSubmitting(false);
    }
  };

  const isSliderQuestion = (question) => {
    const sliderCodes = ["cigarettes_per_day"];
    return (
      question.input_type === "number" &&
      sliderCodes.includes(question.question_code)
    );
  };

  const renderField = (question) => {
    if (question.input_type === "select") {
      return (
        <fieldset className="checkin-page__fieldset">
          <div className="checkin-page__option-group">
            {question.options?.map((opt) => (
              <label key={opt.option_value} className="checkin-page__option">
                <input
                  type="radio"
                  name={question.question_code}
                  value={opt.option_value}
                  checked={answers[question.question_code] === opt.option_value}
                  onChange={(e) =>
                    handleChange(question.question_code, e.target.value)
                  }
                />
                <span>{opt.option_label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    if (isSliderQuestion(question)) {
      const sliderValue = Number(answers[question.question_code] ?? 0);

      return (
        <div className="checkin-page__slider-wrap">
          <div className="checkin-page__slider-header">
            <span className="checkin-page__slider-min">0</span>
            <span className="checkin-page__slider-value">
              {sliderValue} cigarettes/day
            </span>
            <span className="checkin-page__slider-max">40</span>
          </div>

          <input
            className="checkin-page__slider"
            type="range"
            min="0"
            max="40"
            step="1"
            value={sliderValue}
            onChange={(e) =>
              handleChange(question.question_code, e.target.value)
            }
            aria-label={question.question_text}
          />
        </div>
      );
    }

    if (question.input_type === "number") {
      return (
        <>
          <input
            className="checkin-page__input"
            type="number"
            min="0"
            value={answers[question.question_code] || ""}
            onChange={(e) =>
              handleChange(question.question_code, e.target.value)
            }
          />

          {question.ranges?.length > 0 && (
            <ul className="checkin-page__range-list">
              {question.ranges.map((range, idx) => (
                <li key={idx}>
                  {range.range_label} (score {range.score_value})
                </li>
              ))}
            </ul>
          )}
        </>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <main className="checkin-page" aria-label="Relapse risk check-in">
        <div className="checkin-page__container">
          <p className="checkin-page__status">Loading check-in questions...</p>
        </div>
      </main>
    );
  }

  if (!groupedDomains.length) {
    return (
      <main className="checkin-page" aria-label="Relapse risk check-in">
        <div className="checkin-page__container">
          <p className="checkin-page__status">
            No check-in questions available.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="checkin-page" aria-label="Relapse risk check-in">
      <div className="checkin-page__container">
        <NavLink
          to={backTarget}
          className="checkin-page__back-link"
          aria-label={backLabel}
        >
          <ArrowLeft className="checkin-page__back-icon" aria-hidden="true" />
          <span>{backLabel}</span>
        </NavLink>

        <header className="checkin-page__header">
          <h1 className="checkin-page__heading">Smoking Risk Check-In</h1>
          <p className="checkin-page__message">
            Complete the check-in one section at a time. You can leave some
            questions unanswered and still submit at the end.
          </p>
        </header>

        <div className="checkin-page__progress-card">
          <div className="checkin-page__progress-top">
            <div>
              <p className="checkin-page__progress-text">
                Section {currentDomainIndex + 1} of {totalDomains}
              </p>
              <h2 className="checkin-page__domain-title">
                {currentDomain.domain_label}
              </h2>
              <p className="checkin-page__progress-meta">
                Answered {answeredCount} of {questions.length} questions
              </p>
            </div>

            {!isLastStep && (
              <button
                type="button"
                className="checkin-page__skip-button"
                onClick={handleSkipDomain}
              >
                Skip
              </button>
            )}
          </div>

          <div
            className="checkin-page__progress-bar"
            aria-label="Check-in progress"
          >
            <div
              className="checkin-page__progress-fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <form className="checkin-page__form" onSubmit={handleSubmit}>
          {currentDomain.questions.map((q) => (
            <section key={q.question_id} className="checkin-page__field">
              <div className="checkin-page__question-head">
                <label className="checkin-page__label">{q.question_text}</label>
              </div>

              {renderField(q)}
            </section>
          ))}

          {error && <p className="checkin-page__error">{error}</p>}

          <div className="checkin-page__actions">
            {!isFirstStep && (
              <CTAButton
                type="button"
                label="Previous"
                onClick={handlePrevious}
              />
            )}

            {!isLastStep && (
              <CTAButton
                type="button"
                label="Next section"
                onClick={handleNext}
              />
            )}

            {isLastStep && (
              <CTAButton
                type="submit"
                label={submitting ? "Submitting..." : "Submit check-in"}
                disabled={submitting}
              />
            )}
          </div>
        </form>
      </div>
    </main>
  );
};

function formatDomainLabel(domainCode) {
  if (!domainCode) return "General";

  return domainCode
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default CheckInPage;
